import { forwardRef, type InputHTMLAttributes } from 'react'

/* ==========================================================================
   Input Component
   Based on design.md specifications
   Task F30: Updated to use pharmacy colors for focus states
   ========================================================================== */

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Error state styling */
  error?: boolean
}

/**
 * Text input component with focus, disabled, and error states.
 *
 * @example
 * ```tsx
 * <Input placeholder="Enter email" />
 * <Input type="password" disabled />
 * <Input error placeholder="Invalid input" />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error = false, disabled, className = '', ...props }, ref) => {
    const baseStyles = [
      // Layout
      'w-full',
      'px-3 py-3',
      // Typography
      'text-base',
      'font-sans',
      'text-[var(--color-charcoal)]',
      'placeholder:text-[var(--color-gray-500)]',
      // Background and border
      'bg-white',
      'border',
      'rounded-md',
      // Transitions
      'transition-[border-color,box-shadow] duration-150 ease-in-out',
      // Focus state
      'focus:outline-none',
    ]

    const stateStyles = error
      ? [
          // Error state (not customizable per design.md)
          'border-[var(--color-error)]',
          'focus:border-[var(--color-error)]',
          'focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]',
        ]
      : [
          // Default state - uses pharmacy colors for white-labeling (Task F30)
          'border-[var(--color-gray-300)]',
          'focus:border-[var(--pharmacy-primary)]',
          'focus:shadow-[0_0_0_3px_var(--pharmacy-focus-ring)]',
        ]

    const disabledStyles = disabled
      ? [
          'bg-[var(--color-gray-100)]',
          'text-[var(--color-gray-500)]',
          'cursor-not-allowed',
        ]
      : []

    const classes = [
      ...baseStyles,
      ...stateStyles,
      ...disabledStyles,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <input
        ref={ref}
        disabled={disabled}
        className={classes}
        aria-invalid={error || undefined}
        {...props}
      />
    )
  }
)

Input.displayName = 'Input'

export default Input
